import { useEffect, useRef, useState } from 'react';

const MARK_READ_RETRY_MS = 10_000;

type MarkMessageReadFn = (options: {
  variables: { messageId: string };
}) => Promise<{ data?: { markMessageRead?: boolean | null } | null }>;

/**
 * Marks a thread read when opened and whenever unreadCount rises again.
 * Retries on network/GraphQL errors and on a resolved `markMessageRead: false`.
 * Latches success only when the API returns true and unreadCount is zero so a
 * silent false or new unread arriving during an in-flight request cannot block
 * follow-up mark-read attempts.
 *
 * In-flight responses from a previous thread are ignored for latch/retry state
 * so navigation cannot wedge the active thread or reschedule retries elsewhere.
 */
export function useMarkThreadReadOnOpen(
  threadId: string | undefined,
  messageLoaded: boolean,
  unreadCount: number,
  markMessageRead: MarkMessageReadFn
) {
  const markedReadRef = useRef<string | null>(null);
  const markReadInFlightRef = useRef<string | null>(null);
  const deferredMarkReadRef = useRef(false);
  const unreadCountRef = useRef(unreadCount);
  const threadIdRef = useRef(threadId);
  const prevThreadIdRef = useRef(threadId);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const [markReadRetry, setMarkReadRetry] = useState(0);

  threadIdRef.current = threadId;
  unreadCountRef.current = unreadCount;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (retryTimeoutRef.current !== null) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (prevThreadIdRef.current !== threadId) {
      prevThreadIdRef.current = threadId;
      markedReadRef.current = null;
      deferredMarkReadRef.current = false;
      markReadInFlightRef.current = null;
      if (retryTimeoutRef.current !== null) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    }

    if (!threadId || !messageLoaded) return;
    if (markedReadRef.current === threadId && unreadCount === 0) return;
    if (markReadInFlightRef.current === threadId) {
      deferredMarkReadRef.current = true;
      return;
    }

    const attemptedId = threadId;
    markReadInFlightRef.current = attemptedId;

    const clearInFlight = () => {
      if (markReadInFlightRef.current === attemptedId) {
        markReadInFlightRef.current = null;
      }
    };

    const bumpRetry = () => {
      if (!isMountedRef.current) return;
      setMarkReadRetry((n) => n + 1);
    };

    const scheduleRetry = () => {
      if (threadIdRef.current !== attemptedId) return;
      if (markedReadRef.current === attemptedId) {
        markedReadRef.current = null;
      }
      if (retryTimeoutRef.current !== null) {
        clearTimeout(retryTimeoutRef.current);
      }
      retryTimeoutRef.current = setTimeout(() => {
        retryTimeoutRef.current = null;
        if (threadIdRef.current === attemptedId) {
          bumpRetry();
        }
      }, MARK_READ_RETRY_MS);
    };

    const flushDeferredMarkRead = () => {
      if (!deferredMarkReadRef.current) return;
      deferredMarkReadRef.current = false;
      bumpRetry();
    };

    markMessageRead({ variables: { messageId: attemptedId } })
      .then((result) => {
        clearInFlight();
        const stillRelevant = threadIdRef.current === attemptedId;

        if (result.data?.markMessageRead === true) {
          if (stillRelevant) {
            if (unreadCountRef.current === 0) {
              markedReadRef.current = attemptedId;
            } else {
              deferredMarkReadRef.current = true;
            }
            flushDeferredMarkRead();
          }
          return;
        }

        if (stillRelevant) {
          scheduleRetry();
          flushDeferredMarkRead();
        }
      })
      .catch(() => {
        clearInFlight();
        if (threadIdRef.current === attemptedId) {
          scheduleRetry();
          flushDeferredMarkRead();
        }
      });
  }, [threadId, messageLoaded, unreadCount, markMessageRead, markReadRetry]);
}
